import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import NewPage from "../../../layouts/page/NewPage";
import Form from "./Form";
import MenuConfig from "../../../../constants/MenuConfig";
import Api from "../../../../constants/Api";
import { getId, sendToApi } from "../../../../utils/ApiUtils";
import { convertBooleanToStatus } from "../../../../utils/ConverterUtils";
import { formatDateToYYYYMMDD } from "../../../../utils/DateUtils";
import { ApiError } from "../../../components/Common/ApiError";
import { toBackendScope, requiresTarget } from "./ScopeOptions";

const CommissionRuleNew = () => {

    const { t } = useTranslation();
    const [status, setStatus] = useState(true);
    const [entityData, setEntityData] = useState(null);
    const navigate = useNavigate();

    const initialValues = {
        scopeType: null,
        scope: null,
        employee: null,
        branch: null,
        percent: '',
        paymentType: 'FIXED',
        // A primeira faixa nasce travada em zero para a tabela sempre cobrir o mês fraco.
        tiers: [{ minRevenue: 0, percent: '' }],
        validFrom: new Date(),
        validTo: null
    };

    const formSchema = Yup.object().shape({
        scopeType: Yup.object().nullable().required(t('validation.required.scope')),
        // O percentual único só é exigido no modo fixo: no escalonado quem paga é a faixa.
        percent: Yup.number().nullable().when('paymentType', {
            is: (paymentType) => paymentType !== 'TIERED',
            then: (schema) => schema
                .typeError(t('validation.custom.commission.rule.percent.invalid'))
                .min(0, t('validation.custom.commission.rule.percent.invalid'))
                .max(100, t('validation.custom.commission.rule.percent.invalid'))
                .required(t('validation.required.commission'))
        }),
        // A tabela é validada como conjunto: o backend repete estas checagens, mas errar aqui
        // custa uma ida ao servidor para descobrir que faltou preencher uma linha.
        tiers: Yup.array().when('paymentType', {
            is: 'TIERED',
            then: (schema) => schema.test(
                'faixas-validas',
                t('validation.custom.commission.rule.tier.required'),
                (tiers) => Array.isArray(tiers)
                    && tiers.length > 0
                    && tiers.every(tier => tier.percent !== '' && tier.percent != null
                        && tier.minRevenue !== '' && tier.minRevenue != null)
                    && new Set(tiers.map(tier => Number(tier.minRevenue))).size === tiers.length
            )
        }),
        validFrom: Yup.date().nullable().required(t('validation.required.validFrom')),
        // O alvo é obrigatório em produto e categoria, mas não existe em "todos".
        scope: Yup.object().nullable().when('scopeType', {
            is: (scopeType) => requiresTarget(scopeType?.value),
            then: (schema) => schema.required(t('validation.custom.commission.rule.scope.required'))
        }),
        // O fim da vigência é opcional (regra vigente por prazo indeterminado), mas quando
        // informado não pode ser anterior ao início, senão a regra nunca valeria.
        validTo: Yup.date().nullable().min(
            Yup.ref('validFrom'),
            t('validation.custom.commission.rule.period.invalid')
        )
    });

    function onReset() {
        formik.resetForm({ values: initialValues });
        setStatus(true);
    }

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: formSchema,
        onSubmit: values => {
            let payload = {
                id: getId(entityData),
                ...toBackendScope(values.scopeType?.value),
                // Só produto e categoria têm alvo. Nos demais escopos, enviar nulo é o que diz
                // ao backend para limpar o alvo de uma regra que era específica e foi editada.
                scopeId: requiresTarget(values.scopeType?.value) ? values.scope?.value : null,
                employee: values.employee ? { id: values.employee.value } : null,
                branch: values.branch ? { id: values.branch.value } : null,
                paymentType: values.paymentType,
                // No escalonado o percentual único não significa nada, e mandar o valor antigo
                // deixaria um número obsoleto gravado na regra.
                percent: values.paymentType === 'TIERED' ? null : values.percent,
                tiers: values.paymentType === 'TIERED'
                    ? values.tiers.map(tier => ({
                        minRevenue: Number(tier.minRevenue) || 0,
                        percent: Number(tier.percent) || 0
                    }))
                    : [],
                validFrom: values.validFrom ? formatDateToYYYYMMDD(values.validFrom) : null,
                validTo: values.validTo ? formatDateToYYYYMMDD(values.validTo) : null,
                status: convertBooleanToStatus(status)
            }

            sendToApi(Api.sales.commission_rule, payload, () => {
                if (entityData == null) {
                    onReset();
                } else {
                    navigate(MenuConfig.sales.commission_rule.route);
                }
            }, (error) => {
                // Sobreposição de vigência é barrada no backend, que é onde a checagem tem
                // acesso a todas as regras já gravadas. A mensagem dele chega pronta aqui.
                ApiError(error);
                formik.setSubmitting(false);
            });
        },
    });

    const onSubmit = (e) => {
        e.preventDefault();
        formik.handleSubmit();
    }

    return (
        <>
            <NewPage
                motherMenu={t('sales')}
                childMenu={t('commission_rules')}
                activeMenu={t('new')}
                child={<Form formik={formik} status={status} setStatus={setStatus} entityData={entityData} />}
                setEntityData={setEntityData}
                path={Api.sales.commission_rule}
                isSubmitting={formik.isSubmitting}
                handleSubmit={onSubmit}
                cancelRoute={MenuConfig.sales.commission_rule.route}
            />
        </>
    );
}

export default CommissionRuleNew;
